/**
 * IntakeDrawer — the audit's inbox. Drop an email (.eml), paste one, jot a
 * note, log an ad-hoc finding, or pin a regulation citation. Everything lands
 * on the audit's record, searchable by the copilot and preserved as evidence
 * of the work. No forms, no ceremony.
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { PhiUploadNotice } from './BinderPanel.jsx';

const API = process.env.REACT_APP_API_URL;

const KINDS = [
  ['note', 'Note', 'bg-slate-100 text-slate-600'],
  ['email', 'Email', 'bg-sky-50 text-sky-700'],
  ['finding', 'Finding', 'bg-amber-50 text-amber-700'],
  ['regulation', 'Regulation', 'bg-violet-50 text-violet-700'],
];
const KIND_STYLE = Object.fromEntries(KINDS.map(([k, , s]) => [k, s]));
const KIND_LABEL = Object.fromEntries(KINDS.map(([k, l]) => [k, l]));

export default function IntakeDrawer({ auditId }) {
  const [notes, setNotes] = useState([]);
  const [open, setOpen] = useState(false);
  const [adding, setAdding] = useState(false);
  const [kind, setKind] = useState('note');
  const [body, setBody] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [dragOver, setDragOver] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const r = await fetch(`${API}/api/audit/audits/${auditId}/notes`, { credentials: 'include' }).then(r => r.json());
      if (r.success) setNotes(r.notes || []);
    } catch { /* leave as is */ }
  }, [auditId]);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!body.trim()) return;
    setBusy(true); setErr('');
    try {
      const r = await fetch(`${API}/api/audit/audits/${auditId}/notes`, {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ kind, body }),
      }).then(r => r.json());
      if (!r.success) throw new Error(r.message || 'Could not save');
      setBody(''); setAdding(false); await load();
    } catch (e) { setErr(e.message); }
    setBusy(false);
  };

  const uploadFile = async (file) => {
    setBusy(true); setErr('');
    try {
      const fd = new FormData(); fd.append('file', file);
      const r = await fetch(`${API}/api/audit/audits/${auditId}/notes/upload`, { method: 'POST', credentials: 'include', body: fd }).then(r => r.json());
      if (!r.success) throw new Error(r.message || 'Upload failed');
      await load();
    } catch (e) { setErr(e.message); }
    setBusy(false);
  };

  const remove = async (id) => {
    try { await fetch(`${API}/api/audit/audits/${auditId}/notes/${id}`, { method: 'DELETE', credentials: 'include' }); await load(); } catch { /* refresh next load */ }
  };

  return (
    <div
      className={`bg-white border rounded-xl p-4 space-y-2 ${dragOver ? 'border-orange-400 bg-orange-50/40' : 'border-slate-200'}`}
      onDragOver={e => { e.preventDefault(); setDragOver(true); }}
      onDragLeave={() => setDragOver(false)}
      onDrop={e => { e.preventDefault(); setDragOver(false); const f = e.dataTransfer.files?.[0]; if (f) uploadFile(f); }}
    >
      <div className="flex items-center justify-between">
        <button onClick={() => setOpen(o => !o)} className="text-sm font-semibold text-slate-800 flex items-center gap-2">
          Working file
          <span className="text-[11px] font-normal text-slate-400">{notes.length ? `${notes.length} item${notes.length === 1 ? '' : 's'}` : 'notes, emails, findings, regs'}</span>
          <svg className={`w-3.5 h-3.5 text-slate-400 transition-transform ${open ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
        </button>
        <div className="flex gap-2">
          <input ref={fileRef} type="file" accept=".eml,.txt,.md" className="hidden" onChange={e => { const f = e.target.files?.[0]; if (f) uploadFile(f); e.target.value = ''; }} />
          <button onClick={() => fileRef.current?.click()} disabled={busy} className="text-[11px] font-semibold text-slate-500 hover:text-slate-700">↑ Drop an email (.eml)</button>
          <button onClick={() => { setAdding(a => !a); setOpen(true); }} className="text-[11px] font-semibold text-orange-600">{adding ? 'Cancel' : '+ Add'}</button>
        </div>
      </div>
      {dragOver && <div className="text-[12px] text-orange-600 font-semibold text-center py-1">Drop it — emails and notes land right on the audit.</div>}
      {open && <PhiUploadNotice />}
      {err && <div className="text-xs text-red-600">{err}</div>}

      {adding && (
        <div className="space-y-2 border border-slate-200 rounded-lg p-3 bg-slate-50/60">
          <div className="flex gap-1.5">
            {KINDS.map(([k, l]) => (
              <button key={k} onClick={() => setKind(k)} className={`text-[11px] px-2.5 py-1 rounded-full font-semibold ${kind === k ? 'bg-orange-600 text-white' : KIND_STYLE[k]}`}>{l}</button>
            ))}
          </div>
          <textarea
            value={body} onChange={e => setBody(e.target.value)} rows={4}
            placeholder={kind === 'email' ? 'Paste the full email here — headers and all. It gets parsed automatically.'
              : kind === 'finding' ? 'What did you observe? It becomes part of the audit record.'
              : kind === 'regulation' ? 'The citation and why it matters here, e.g., 42 CFR §422.504(i) — FDR oversight obligations.'
              : 'Jot it down. The copilot reads these too.'}
            className="w-full text-[13px] border border-slate-300 rounded-lg px-3 py-2"
          />
          <button onClick={save} disabled={busy || !body.trim()} className="px-3 py-1.5 text-xs font-semibold rounded-lg bg-orange-600 text-white shadow disabled:opacity-50">{busy ? 'Saving…' : 'Save'}</button>
        </div>
      )}

      {open && (
        <div className="space-y-1.5 pt-1">
          {!notes.length && !adding && <div className="text-[12px] text-slate-400">Nothing here yet. Drop an email, paste a reg, jot a note — it all stays with the audit.</div>}
          {notes.map(n => (
            <div key={n.id} className="border border-slate-100 rounded-lg px-3 py-2">
              <div className="flex items-center gap-2">
                <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${KIND_STYLE[n.kind] || KIND_STYLE.note}`}>{KIND_LABEL[n.kind] || n.kind}</span>
                <button onClick={() => setExpanded(expanded === n.id ? null : n.id)} className="text-[12px] font-semibold text-slate-700 truncate text-left flex-1">{n.title || (n.body || '').slice(0, 70) || 'Untitled'}</button>
                <span className="text-[10px] text-slate-400 flex-shrink-0">{new Date(n.created_at).toLocaleDateString()}</span>
                <button onClick={() => remove(n.id)} className="text-slate-300 hover:text-red-500 text-xs flex-shrink-0">×</button>
              </div>
              {expanded === n.id && <div className="text-[12px] text-slate-600 whitespace-pre-wrap mt-1.5 max-h-56 overflow-y-auto">{n.body}</div>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
